import { Link } from "react-router-dom";
import { useResource, formatDateTime, titleCase } from "../../lib/hooks";
import { STAGES, Lead } from "./Pipeline";
import type { Company, Contact, Referral } from "../../lib/types";

interface Activity {
    id: string;
    subject: string;
    body: string | null;
    kind: string;
    occurred_at: string;
}

interface Inquiry {
    id: string;
    first_name: string;
    last_name: string;
    status: string;
    created_at: string;
}

const currency = (n: number): string =>
    n.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });

const Stat = ({ label, value, to }: { label: string; value: string | number; to: string }) => (
    <Link to={to} className="card stat" style={{ textDecoration: "none", color: "inherit", display: "block" }}>
        <div className="label">{label}</div>
        <div className="value">{value}</div>
    </Link>
);

/**
 * The CRM landing page, following the Azure CRM's dashboard: headline counts,
 * pipeline overview by stage, recent leads and recent activity — all live from
 * the same database as ShiftFlow, because there is only one database.
 */
export const CrmDashboard = () => {
    const leads = useResource<Lead[]>("/leads");
    const companies = useResource<Company[]>("/companies");
    const contacts = useResource<Contact[]>("/contacts");
    const inquiries = useResource<Inquiry[]>("/inquiries");
    const activities = useResource<Activity[]>("/activities");
    const referrals = useResource<Referral[]>("/referrals");

    const allLeads = leads.data ?? [];
    const pipelineValue = allLeads
        .filter((l) => !l.stage.startsWith("closed"))
        .reduce((sum, l) => sum + Number(l.estimated_value ?? 0), 0);

    if (leads.error) return <div className="error-banner">{leads.error}</div>;

    return (
        <div style={{ display: "grid", gap: "1.1rem" }}>
            <div className="grid cols-4">
                <Stat label="Open leads" value={allLeads.filter((l) => !l.stage.startsWith("closed")).length} to="/crm/pipeline" />
                <Stat label="Pipeline value" value={currency(pipelineValue)} to="/crm/pipeline" />
                <Stat label="Companies" value={(companies.data ?? []).length} to="/crm/companies" />
                <Stat label="New inquiries" value={(inquiries.data ?? []).filter((i) => i.status === "new").length} to="/crm/inquiries" />
            </div>

            <div className="card">
                <div className="card-head">
                    <h2>Pipeline by stage</h2>
                    <Link to="/crm/pipeline" style={{ fontSize: "0.8rem", fontWeight: 600 }}>Open pipeline →</Link>
                </div>
                {/* The teal ramp deepens as a deal progresses; Closed Won is the
                    one green — the design's "money in the bank" colour. */}
                <div className="card-body" style={{ display: "grid", gap: "0.75rem" }}>
                    {(() => {
                        const BAR_COLOURS: Record<string, string> = {
                            new_inquiry: "#3fdbe3", qualified: "#0d9fa3", proposal_sent: "#0a565a",
                            negotiation: "#073f42", closed_won: "#1f8a3d", closed_lost: "#92a6a6",
                        };
                        const sums = STAGES.map((stage) => ({
                            stage,
                            count: allLeads.filter((l) => l.stage === stage.id).length,
                            value: allLeads.filter((l) => l.stage === stage.id)
                                .reduce((sum, l) => sum + Number(l.estimated_value ?? 0), 0),
                        }));
                        const max = Math.max(1, ...sums.map((s) => s.value));
                        return sums.map(({ stage, count, value }) => (
                            <div key={stage.id} style={{ display: "flex", alignItems: "center", gap: "0.9rem" }}
                                title={`${count} lead${count === 1 ? "" : "s"}`}>
                                <span style={{ width: 108, fontSize: "0.82rem", fontWeight: 600, flexShrink: 0 }}>{stage.label}</span>
                                <div style={{ flex: 1, height: 14, borderRadius: 999, background: "var(--surface-sunk)", overflow: "hidden" }}>
                                    <div style={{
                                        width: `${Math.round((value / max) * 100)}%`,
                                        minWidth: value > 0 ? 14 : 0, height: "100%", borderRadius: 999,
                                        background: BAR_COLOURS[stage.id] ?? "var(--brand)",
                                    }} />
                                </div>
                                <span className="mono" style={{ width: 62, textAlign: "right", fontWeight: 600, fontSize: "0.82rem", flexShrink: 0 }}>
                                    {value >= 1000 ? `$${Math.round(value / 1000)}k` : `$${value}`}
                                </span>
                            </div>
                        ));
                    })()}
                </div>
            </div>

            <div className="grid cols-2">
                <div className="card">
                    <div className="card-head">
                        <h2>Recent leads</h2>
                        <Link to="/crm/pipeline" style={{ fontSize: "0.78rem" }}>Pipeline →</Link>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <tbody>
                                    {allLeads.slice(0, 6).map((lead) => (
                                        <tr key={lead.id}>
                                            <td style={{ fontWeight: 600 }}>{lead.title}</td>
                                            <td>
                                                <span className="chip info">
                                                    {STAGES.find((s) => s.id === lead.stage)?.label ?? lead.stage}
                                                </span>
                                            </td>
                                            <td className="mono" style={{ textAlign: "right" }}>
                                                {lead.estimated_value ? currency(Number(lead.estimated_value)) : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                    {allLeads.length === 0 && (
                                        <tr><td className="empty">No leads yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Recent activity</h2></div>
                    <div className="card-body">
                        {(activities.data ?? []).slice(0, 6).map((activity) => (
                            <div key={activity.id} className="feed-item">
                                <h4>
                                    <span className="chip mute" style={{ marginRight: 6 }}>{titleCase(activity.kind)}</span>
                                    {activity.subject}
                                </h4>
                                {activity.body && <p>{activity.body}</p>}
                                <time>{formatDateTime(activity.occurred_at)}</time>
                            </div>
                        ))}
                        {(activities.data ?? []).length === 0 && (
                            <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No activity recorded.</p>
                        )}
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-head">
                    <h2>Service intake</h2>
                    <Link to="/referrals" style={{ fontSize: "0.78rem" }}>Referrals →</Link>
                </div>
                <div className="card-body">
                    <p style={{ fontSize: "0.85rem", color: "var(--muted)" }}>
                        {(referrals.data ?? []).filter((r) => r.status === "new").length} new referral(s) waiting,{" "}
                        {(contacts.data ?? []).length} contacts on file. Referrals, incidents and policies are
                        shared with ShiftFlow — the two surfaces read the same records.
                    </p>
                </div>
            </div>
        </div>
    );
};
